//networkWeights.ts — the vendored Anime4K CNN-x2-S "Real Life" weights, packed into the
//exact byte layout the WGSL uniform structs expect. See weights/README.md for provenance.

import raw from './weights/anime4k-cnn-2x-s-rl.json'

const TAPS = 9
const CHANNELS = 4
//The first layer reads the source directly; the rest read a CReLU split, so twice the taps.
const FIRST_KERNEL_FLOATS = TAPS * CHANNELS * CHANNELS
const HIDDEN_KERNEL_FLOATS = 2 * FIRST_KERNEL_FLOATS

interface RawLayer {
	readonly weights: readonly number[]
	readonly bias: readonly number[]
}

export interface NetworkWeights {
	//Kernels then bias, contiguous — matches `struct ConvParams` in networkShaders.ts.
	readonly first: Float32Array
	readonly hidden: readonly Float32Array[]
}

export function loadNetworkWeights(): NetworkWeights {
	const { layers } = raw
	return {
		first: packLayer(layers.conv2d_tf, 'conv2d_tf', FIRST_KERNEL_FLOATS),
		hidden: [
			packLayer(layers.conv2d_1_tf, 'conv2d_1_tf', HIDDEN_KERNEL_FLOATS),
			packLayer(layers.conv2d_2_tf, 'conv2d_2_tf', HIDDEN_KERNEL_FLOATS),
			packLayer(layers.conv2d_last_tf, 'conv2d_last_tf', HIDDEN_KERNEL_FLOATS),
		],
	}
}

//Bad weights would otherwise show up as a subtly wrong picture rather than an error.
function packLayer(layer: RawLayer, name: string, kernelFloats: number): Float32Array {
	assertFloats(layer.weights, `${name}.weights`, kernelFloats)
	assertFloats(layer.bias, `${name}.bias`, CHANNELS)
	const packed = new Float32Array(kernelFloats + CHANNELS)
	packed.set(layer.weights, 0)
	packed.set(layer.bias, kernelFloats)
	return packed
}

function assertFloats(values: readonly number[], label: string, expected: number): void {
	if (values.length !== expected) {
		throw new Error(`Weights ${label}: expected ${expected} values, got ${values.length}`)
	}
	if (!values.every((value) => Number.isFinite(value))) {
		throw new Error(`Weights ${label}: contains a non-finite value`)
	}
}
